import cx from "classnames";
import { useTranslation } from "i18n";
import ProfileCard from "pages/Profile/components/common/ProfileCard";
import { useGetTraining } from "pages/Profile/hooks/useGetTraining";
import { usePublicView } from "pages/Profile/hooks/usePublicView";
import { useEffect, useMemo, useState } from "react";
import ExpandableTextArea from "../common/ExpandableTextArea";
import { dataTestIds } from "data-testids";
import { useUpdateTraining } from "pages/Profile/hooks/useUpdateTraining";
import { TRAINING_DISPLAY_LINE_THRESHOLD } from "../ProfileHeader/constants";
import {
  buildHighlightsRequestPayload,
  expandableTextAreaStorageHtmlToDraftString,
} from "shared/utils/dom/sanitizeExpandableTextAreaHtml";

const MAX_CONTENT_LENGTH = 1500;
const WARNING_CONTENT_LENGTH = 1000;
const ATTENTION_CONTENT_LENGTH = MAX_CONTENT_LENGTH;

interface Props {
  isEdit?: boolean;
}

const TrainingSection = ({ isEdit }: Props) => {
  const { t } = useTranslation();
  const { isPublicView } = usePublicView();
  const { isLoading: isGetTrainingLoading, notes = "" } = useGetTraining();

  const { mutate, isLoading: isUpdateTrainingLoading } = useUpdateTraining();

  const [editExpandableTextArea, setEditExpandableTextArea] = useState(false);
  const [editExpandableTextAreaError, setEditExpandableTextAreaError] =
    useState<string | null>(null);
  const [draftContent, setDraftContent] = useState("");

  const isTrainingSectionHidden = !notes && !isEdit;
  const isNoData = !notes && !isPublicView;

  const isLoading = isGetTrainingLoading || isUpdateTrainingLoading;

  const notesText = editExpandableTextArea ? draftContent : notes;
  const draftNotesPayload = useMemo(
    () => buildHighlightsRequestPayload(draftContent),
    [draftContent]
  );
  const savedNotesPayload = useMemo(
    () => buildHighlightsRequestPayload(notes),
    [notes]
  );

  const notesInvalid = draftNotesPayload.length > MAX_CONTENT_LENGTH;

  const isSaveButtonDisabled =
    notesInvalid ||
    draftNotesPayload === savedNotesPayload ||
    isUpdateTrainingLoading;

  const hasContent = Boolean(editExpandableTextArea ? draftContent : notes);

  useEffect(() => {
    setDraftContent(notes);
  }, [notes]);

  useEffect(() => {
    setEditExpandableTextArea(false);
  }, [isEdit]);

  const handleToggleEdit = () => {
    setEditExpandableTextArea((prevState) => !prevState);
  };

  const handleCloseEdit = () => {
    setEditExpandableTextArea(false);
    setDraftContent(notes);
    setEditExpandableTextAreaError(null);
  };

  const handleOpenEdit = () => {
    setDraftContent(expandableTextAreaStorageHtmlToDraftString(notes));

    setEditExpandableTextAreaError(null);
    setEditExpandableTextArea(true);
  };

  const handleSave = () => {
    const toSave = draftNotesPayload;

    if (toSave.length > MAX_CONTENT_LENGTH) {
      setEditExpandableTextAreaError(
        t("performer:profile.header.validation.charLimit", {
          maxLenght: MAX_CONTENT_LENGTH,
        })
      );

      return;
    }

    setEditExpandableTextAreaError(null);

    mutate(
      { notes: toSave },
      {
        onSuccess: () => {
          handleCloseEdit();
        },
      }
    );

    setEditExpandableTextArea(false);
  };

  const expandableTextAreaProps = {
    content: expandableTextAreaStorageHtmlToDraftString(notesText),
    hasContent,
    editExpandableTextArea,
    draftContent: expandableTextAreaStorageHtmlToDraftString(draftContent),
    editExpandableTextAreaError,
    isSaveButtonDisabled,
    isEdit,
    isPublicView,
    alignWithVideoColumn: true,
    onOpenEdit: handleOpenEdit,
    onCloseEdit: handleCloseEdit,
    onSave: handleSave,
    onDraftChange: setDraftContent,
    isEditButtonVisible: false,
    lineClamp: TRAINING_DISPLAY_LINE_THRESHOLD,
    maxLength: MAX_CONTENT_LENGTH,
    warningLength: WARNING_CONTENT_LENGTH,
    attentionLength: ATTENTION_CONTENT_LENGTH,
    isEmptyFieldCentered: true,
    textareaClassName: "h-[420px]",
    placeholder: t("performer:profile.training.noData.content"),
    emptyFieldText: t("performer:profile.training.noData.content"),
    emptyFieldClassName: "italic dark:text-white/60",
  };

  if (isTrainingSectionHidden) {
    return null;
  }

  return (
    <ProfileCard
      className={cx("dark:text-white/90", { "grid min-h-[141px]": isNoData })}
      isEdit={isEdit}
      isLoading={isLoading}
      onClickEdit={handleToggleEdit}
      data-testid={dataTestIds.profile["Profile.ProfileCard.trainingSection"]}
    >
      <div className="flex flex-col gap-4">
        <h3 className="text-m uppercase dark:text-white">
          {t("performer:profile.training.title")}
        </h3>
        <div className="flex flex-1 items-center justify-center">
          <ExpandableTextArea
            {...expandableTextAreaProps}
            data-testid={`${
              dataTestIds.profile["Profile.ProfileCard.trainingSection"]
            }-content`}
          />
        </div>
      </div>
    </ProfileCard>
  );
};

export default TrainingSection;
